import React, { useEffect, useRef, useState } from 'react';
import ImageSelectionModal from '../../pages/FormMenuItem/ImageSelectionModal';
import { useDispatch, useSelector } from 'react-redux';
import { addBanner, updateBanner } from '../../redux/action'; 
import LocalizedContentFields from '../LocalizedContentFields/LocalizedContentFields';
import { emptyContentTranslations } from '../../i18n/contentTranslationApi';

function BannerForm() {
    const edit = useSelector((state) => state?.admin?.edit);
    const bannerSaving = useSelector((state) => Boolean(state?.admin?.bannerSaving));
    const bannersObj = useSelector((state) => state?.admin?.banners) || [];
    const existingBanners = Array.isArray(bannersObj) ? bannersObj : Object.values(bannersObj);
    const e = edit?.bannerId ? edit : { bannerId: '', bannerImage: '', bannerName: '' };
    
    const [bannerName, setName] = useState(e.bannerName);
    const [thumbnailUrl, setThumbnailUrl] = useState(e.bannerImage); 
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [translations, setTranslations] = useState(emptyContentTranslations);
    const submitLock = useRef(false);
    
    const dispatch = useDispatch();

    useEffect(() => {
        if (!bannerSaving) submitLock.current = false;
    }, [bannerSaving]);

    useEffect(() => {
        if (edit?.bannerId) {
            setName(edit.bannerName);
            setThumbnailUrl(edit.bannerImage);
        }else {
        // 🚀 Reset local form fields to blank if no edit ID is present!
        setName('');
        setThumbnailUrl('');
    }
    }, [edit]);

    // const validateForm = () => {
    //     const newErrors = {};
    //     if (!bannerName.trim()) newErrors.bannerName = 'Name is required';
    //     if (!thumbnailUrl) newErrors.bannerImage = 'Banner image is required';
    //     setErrors(newErrors);
    //     return Object.keys(newErrors).length === 0;
    // };

    const validateForm = () => {
        const newErrors = {};
        const trimmedName = bannerName.trim().toLowerCase();

        if (!trimmedName) {
            newErrors.bannerName = 'Name is required';
        } else {
            //  Uniqueness Check Logic
            const isDuplicate = existingBanners.some((banner) => {
                if (edit?.bannerId) {
                    // Edit Mode: Apne id ko chhodkar baki sabse check karein
                    return banner.bannerId !== edit.bannerId && banner.bannerName.toLowerCase() === trimmedName;
                } else {
                    // Add Mode: Sabhi se check karein
                    return banner.bannerName.toLowerCase() === trimmedName;
                }
            });

            if (isDuplicate) {
                newErrors.bannerName = 'This banner name already exists. Please choose a unique name.';
            }
        }

        if (!thumbnailUrl) {
            newErrors.bannerImage = 'Banner image is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (bannerSaving || submitLock.current) return;
        
        if (validateForm()) {
            submitLock.current = true;
            // 🚀 Send data as a clean standard JavaScript Plain Object
            const bannerData = {
                bannerName: bannerName.trim(),
                bannerImage: thumbnailUrl, // Contains either Base64 string or gallery URL string
                displayOnMenu: false,
                isActive: true
                ,translations
            };

            if (edit?.bannerId) {
                bannerData.bannerId = edit.bannerId;
                bannerData.originalBannerImage = edit.bannerImage;
                dispatch(updateBanner(bannerData)); 
            } else {
                dispatch(addBanner(bannerData)); 
            }
        }
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                // 🚀 This guarantees a pure Base64 DataURL string format instead of a broken local blob link!
                setThumbnailUrl(reader.result); 
                setErrors((prev) => ({ ...prev, bannerImage: '' }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleImageSelected = (selectedImageUrl) => {
        setThumbnailUrl(selectedImageUrl);
        setErrors((prev) => ({ ...prev, bannerImage: '' }));
        closeImageModal();
    };

    const openImageModal = () => setIsModalOpen(true);
    const closeImageModal = () => setIsModalOpen(false);

    return (
        <div style={{margin: '0 auto', padding: '10px' }}>
            <form id="banner-form" onSubmit={handleSubmit} style={styles.form}>
                
                <div style={styles.formGroup}>
                    <label htmlFor="bannerName" style={styles.label}>Banner Name:</label>
                    <input
                        type="text"
                        id="bannerName"
                        value={bannerName}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Weekend Special Discount"
                        style={styles.input}
                    />
                    {errors.bannerName && <p style={styles.errorText}>{errors.bannerName}</p>}
                </div>

                <LocalizedContentFields
                    entityType="banner"
                    entityId={edit?.bannerId}
                    value={translations}
                    onChange={setTranslations}
                    fields={[
                        { name: 'name', label: 'Translated banner name', placeholder: 'Enter translated banner name' }
                    ]}
                />

                <div>
                    <label style={styles.labelStyle}>Banner Graphic Asset:</label>
                    <input
                        type="file"
                        id="imageUpload"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={{ display: 'none' }}
                    />
                    <div style={{ display: 'flex', gap: '10px', marginTop: '5px' }}>
                        <label htmlFor="imageUpload" style={styles.browseButtonStyle}>
                            📁 Upload From Device
                        </label>
                    </div>
                    {errors.bannerImage && <p style={styles.errorText}>{errors.bannerImage}</p>}
                </div>

                {thumbnailUrl && (
                    <div style={styles.thumbnailContainerStyle}>
                        <p style={styles.labelStyle}>Preview Thumbnail:</p>
                        <img
                            src={thumbnailUrl}
                            alt="Dynamic Banner Rendering Track"
                            style={styles.thumbnailStyle}
                        />
                    </div>
                )}

            </form>

            {/* {isModalOpen && (
                <ImageSelectionModal
                    onClose={closeImageModal}
                    onSelectImage={handleImageSelected}
                />
            )} */}
        </div>
    );
}

const styles = {
    form: { display: 'flex', flexDirection: 'column', gap: '15px' },
    formGroupStyle: { marginBottom: '1px' },
    formGroup: { marginBottom: '2px', textAlign: 'left' },
    label: { display: 'block', marginBottom: '6px', fontWeight: 'bold', color: '#333' },
    labelStyle: { display: 'block', marginBottom: '3px', fontWeight: 'bold', color: '#333', fontSize: '14px' },
    input: { width: '100%', padding: '10px', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' },
    thumbnailContainerStyle: {textAlign: 'left' },
    thumbnailStyle: { width: '100%', aspectRatio: '16/9', maxHeight: '180px', border: '1px solid #ddd', borderRadius: '6px', objectFit: 'cover' },
    browseButtonStyle: { backgroundColor: '#0078d4', color: 'white', padding: '10px 14px', borderRadius: '4px', cursor: 'pointer', fontWeight: '500', fontSize: '13px' },
    galleryButtonStyle: { backgroundColor: '#2b2b2b', color: 'white', padding: '10px 14px', borderRadius: '4px', cursor: 'pointer', border: 'none', fontWeight: '500', fontSize: '13px' },
    button: { padding: '12px 20px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '15px', fontWeight: 'bold', marginTop: '10px' },
    errorText: { color: '#ef4444', fontSize: '12px', fontWeight: '500', marginTop: '4px', textAlign: 'left' }
};

export default BannerForm;
